/**
 * 图片分辨率限制模块
 * 基于目标字符数动态计算最大分辨率，确保渲染流畅
 *
 * @module performance/imageScale
 * @created 2026-04-14 - Phase 8 Wave 8.1
 */

import { isResolutionLimitEnabled, getDefaultMaxChars } from './features'

/**
 * 缩放配置
 */
export interface ScaleConfig {
  /** 最大字符数上限，默认10000 */
  maxChars: number
  /** 当前fontSize */
  fontSize: number
  /** 当前charSpacing */
  charSpacing: number
  /** 用户选择保持原尺寸（跳过缩放） */
  preserveOriginal?: boolean
}

/**
 * 缩放结果
 */
export interface ScaleResult {
  /** 缩放后的canvas（可直接用于渲染） */
  canvas: HTMLCanvasElement
  /** 原始宽度 */
  originalWidth: number
  /** 原始高度 */
  originalHeight: number
  /** 缩放后宽度 */
  scaledWidth: number
  /** 缩放后高度 */
  scaledHeight: number
  /** 缩放比例（1表示未缩放） */
  scaleRatio: number
  /** 预估字符数 */
  charCount: number
  /** 是否被缩放 */
  wasScaled: boolean
}

/**
 * 计算字符宽度
 *
 * @param fontSize - 字体大小
 * @param charSpacing - 字符间距
 * @returns 字符宽度（像素）
 */
export function calculateCharWidth(fontSize: number, charSpacing: number): number {
  return fontSize * 0.6 * charSpacing
}

/**
 * 计算字符高度
 *
 * @param fontSize - 字体大小
 * @returns 字符高度（像素）
 */
export function calculateCharHeight(fontSize: number): number {
  return fontSize
}

/**
 * 计算给定分辨率下的预估字符数
 *
 * @param width - 图片宽度
 * @param height - 图片高度
 * @param fontSize - 字体大小
 * @param charSpacing - 字符间距
 * @returns 预估字符数
 */
export function calculateCharCount(
  width: number,
  height: number,
  fontSize: number,
  charSpacing: number
): number {
  const charWidth = calculateCharWidth(fontSize, charSpacing)
  const charHeight = calculateCharHeight(fontSize)
  const cols = Math.max(1, Math.floor(width / charWidth))
  const rows = Math.max(1, Math.floor(height / charHeight))
  return cols * rows
}

/**
 * 基于字符数上限计算最大分辨率
 *
 * 字符数 = cols × rows ≈ width/charWidth × height/charHeight
 * 设目标字符数为 N，保持纵横比 aspectRatio = width/height
 *
 * cols = sqrt(N × aspectRatio)
 * rows = cols / aspectRatio
 * maxWidth = cols × charWidth
 * maxHeight = rows × charHeight
 *
 * @param sourceWidth - 源图片宽度
 * @param sourceHeight - 源图片高度
 * @param config - 缩放配置
 * @returns 最大分辨率和预估字符数
 */
export function calculateMaxResolution(
  sourceWidth: number,
  sourceHeight: number,
  config: ScaleConfig
): { maxWidth: number; maxHeight: number; charCount: number } {
  const { maxChars, fontSize, charSpacing } = config

  const charWidth = calculateCharWidth(fontSize, charSpacing)
  const charHeight = calculateCharHeight(fontSize)

  // 计算纵横比
  const aspectRatio = sourceWidth / sourceHeight

  // 计算最大列数和行数（保持纵横比）
  const maxCols = Math.sqrt(maxChars * aspectRatio)
  const maxRows = maxCols / aspectRatio

  // 计算最大分辨率
  const maxWidth = Math.floor(maxCols * charWidth)
  const maxHeight = Math.floor(maxRows * charHeight)

  // 计算实际字符数（可能有轻微偏差）
  const actualCharCount = calculateCharCount(maxWidth, maxHeight, fontSize, charSpacing)

  return {
    maxWidth,
    maxHeight,
    charCount: actualCharCount
  }
}

/**
 * 执行图片缩放
 *
 * @param source - 源图片（HTMLImageElement或HTMLCanvasElement）
 * @param config - 缩放配置
 * @returns 缩放结果
 */
export function scaleImageToCharLimit(
  source: HTMLImageElement | HTMLCanvasElement,
  config: ScaleConfig
): ScaleResult {
  const sourceWidth = source.width
  const sourceHeight = source.height

  // 检查功能是否启用
  if (!isResolutionLimitEnabled() || config.preserveOriginal) {
    // 功能禁用或用户选择保持原尺寸，不缩放
    const canvas = document.createElement('canvas')
    canvas.width = sourceWidth
    canvas.height = sourceHeight
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)

    return {
      canvas,
      originalWidth: sourceWidth,
      originalHeight: sourceHeight,
      scaledWidth: sourceWidth,
      scaledHeight: sourceHeight,
      scaleRatio: 1,
      charCount: calculateCharCount(sourceWidth, sourceHeight, config.fontSize, config.charSpacing),
      wasScaled: false
    }
  }

  // 计算当前字符数
  const currentCharCount = calculateCharCount(
    sourceWidth,
    sourceHeight,
    config.fontSize,
    config.charSpacing
  )

  // 如果当前字符数已在限制内，不需要缩放
  if (currentCharCount <= config.maxChars) {
    const canvas = document.createElement('canvas')
    canvas.width = sourceWidth
    canvas.height = sourceHeight
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)

    return {
      canvas,
      originalWidth: sourceWidth,
      originalHeight: sourceHeight,
      scaledWidth: sourceWidth,
      scaledHeight: sourceHeight,
      scaleRatio: 1,
      charCount: currentCharCount,
      wasScaled: false
    }
  }

  // 计算目标分辨率
  const { maxWidth, maxHeight, charCount } = calculateMaxResolution(
    sourceWidth,
    sourceHeight,
    config
  )

  // 计算缩放比例
  const scaleRatio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight)

  // 执行缩放
  const scaledWidth = Math.floor(sourceWidth * scaleRatio)
  const scaledHeight = Math.floor(sourceHeight * scaleRatio)

  // 创建缩放后的canvas
  const canvas = document.createElement('canvas')
  canvas.width = scaledWidth
  canvas.height = scaledHeight

  const ctx = canvas.getContext('2d')!
  // 使用高质量缩放
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, scaledWidth, scaledHeight)

  return {
    canvas,
    originalWidth: sourceWidth,
    originalHeight: sourceHeight,
    scaledWidth,
    scaledHeight,
    scaleRatio,
    charCount,
    wasScaled: true
  }
}

/**
 * 从HTMLImageElement创建缩放后的HTMLImageElement
 *
 * @param source - 源图片
 * @param config - 缩放配置
 * @returns 缩放后的HTMLImageElement和缩放信息
 */
export function scaleImageElement(
  source: HTMLImageElement,
  config: ScaleConfig
): { image: HTMLImageElement; scaleResult: ScaleResult } {
  const scaleResult = scaleImageToCharLimit(source, config)

  // 将canvas转换为HTMLImageElement
  const scaledImage = new Image()
  scaledImage.src = scaleResult.canvas.toDataURL('image/png')
  scaledImage.width = scaleResult.scaledWidth
  scaledImage.height = scaleResult.scaledHeight

  return {
    image: scaledImage,
    scaleResult
  }
}

/**
 * 创建默认缩放配置
 *
 * @param fontSize - 字体大小（可选，默认10）
 * @param charSpacing - 字符间距（可选，默认1）
 * @returns 默认配置
 */
export function createDefaultScaleConfig(
  fontSize?: number,
  charSpacing?: number
): ScaleConfig {
  return {
    maxChars: getDefaultMaxChars(),
    fontSize: fontSize ?? 10,
    charSpacing: charSpacing ?? 1,
    preserveOriginal: false
  }
}

/**
 * 格式化缩放信息为用户友好的文本
 *
 * @param scaleResult - 缩放结果
 * @returns 格式化文本
 */
export function formatScaleInfo(scaleResult: ScaleResult): string {
  if (!scaleResult.wasScaled) {
    return `原始尺寸: ${scaleResult.originalWidth}×${scaleResult.originalHeight}，字符数: ${scaleResult.charCount.toLocaleString()}`
  }

  return `已缩放: ${scaleResult.originalWidth}×${scaleResult.originalHeight} → ${scaleResult.scaledWidth}×${scaleResult.scaledHeight} (${Math.round(scaleResult.scaleRatio * 100)}%)，字符数: ${scaleResult.charCount.toLocaleString()}`
}